// Single source of truth for score color and emoji configuration
// This ensures consistency across all components that display scores

export const SCORE_CONFIG = {
  // Color configuration for score badges
  colors: {
    // 0-25: Dark red
    veryLow: {
      background: 'bg-red-800',
      text: 'text-white',
      range: [0, 25]
    },
    // 26-50: Dark yellow/amber
    low: {
      background: 'bg-amber-600', 
      text: 'text-white',
      range: [26, 50]
    },
    // 51-75: Neutral yellow
    medium: {
      background: 'bg-yellow-400',
      text: 'text-black', 
      range: [51, 75]
    },
    // 76-89: Dark green
    high: {
      background: 'bg-green-700',
      text: 'text-white',
      range: [76, 89]
    },
    // 90-100: Bright mint green
    excellent: {
      background: 'bg-emerald-400',
      text: 'text-black',
      range: [90, 100]
    }
  },

  // Emoji configuration for different score ranges
  emojis: {
    // Crown for excellent performance (≥90)
    crown: {
      emoji: '👑',
      range: [90, 100],
      tooltip: 'Außergewöhnliche Leistung'
    },
    // Skull for very poor performance (≤25)
    skull: {
      emoji: '💀', 
      range: [0, 25],
      tooltip: 'Kritische Bewertung'
    },
    // Chart for standard performance (26-89)
    chart: {
      emoji: '📊',
      range: [26, 89], 
      tooltip: 'Standard Bewertung'
    }
  },

  // Qualification-specific emoji configuration (for 0-10 scale)
  qualificationEmojis: {
    excellent: {
      emoji: '⭐',
      range: [9, 10],
      tooltip: 'Sehr gut bestanden'
    },
    good: {
      emoji: '✅', 
      range: [7, 8],
      tooltip: 'Gut bestanden'
    },
    average: {
      emoji: '⚖️',
      range: [5, 6], 
      tooltip: 'Bestanden'
    },
    poor: {
      emoji: '⚠️',
      range: [3, 4],
      tooltip: 'Grenzwertig bestanden'
    },
    veryPoor: {
      emoji: '❌',
      range: [0, 2],
      tooltip: 'Nicht bestanden'
    }
  }
};

// Helper functions to get score styling
export const getScoreColor = (score) => {
  if (score <= 25) return SCORE_CONFIG.colors.veryLow.background;
  if (score <= 50) return SCORE_CONFIG.colors.low.background;
  if (score <= 75) return SCORE_CONFIG.colors.medium.background;
  if (score <= 89) return SCORE_CONFIG.colors.high.background;
  if (score <= 100) return SCORE_CONFIG.colors.excellent.background;
  return SCORE_CONFIG.colors.excellent.background; // fallback
};

export const getScoreTextColor = (score) => {
  if (score <= 25) return SCORE_CONFIG.colors.veryLow.text;
  if (score <= 50) return SCORE_CONFIG.colors.low.text;
  if (score <= 75) return SCORE_CONFIG.colors.medium.text;
  if (score <= 89) return SCORE_CONFIG.colors.high.text;
  if (score <= 100) return SCORE_CONFIG.colors.excellent.text;
  return SCORE_CONFIG.colors.excellent.text; // fallback
};

// Helper functions to get emojis
export const getAnalysisEmoji = (score) => {
  if (score >= 90) return SCORE_CONFIG.emojis.crown.emoji;
  if (score <= 25) return SCORE_CONFIG.emojis.skull.emoji;
  return SCORE_CONFIG.emojis.chart.emoji;
};

export const getAnalysisEmojiTooltip = (score) => {
  if (score >= 90) return SCORE_CONFIG.emojis.crown.tooltip;
  if (score <= 25) return SCORE_CONFIG.emojis.skull.tooltip;
  return SCORE_CONFIG.emojis.chart.tooltip;
};

export const getQualificationEmoji = (score, maxScore = 10) => {
  // Percentage-based so it scales with the configured qualification max (not a fixed 0-10 scale)
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  if (pct <= 20) return SCORE_CONFIG.qualificationEmojis.veryPoor.emoji; // ❌
  if (pct <= 40) return SCORE_CONFIG.qualificationEmojis.poor.emoji; // ⚠️
  if (pct <= 60) return SCORE_CONFIG.qualificationEmojis.average.emoji; // ⚖️
  if (pct <= 80) return SCORE_CONFIG.qualificationEmojis.good.emoji; // ✅
  return SCORE_CONFIG.qualificationEmojis.excellent.emoji; // ⭐
};

export const getQualificationEmojiTooltip = (score, maxScore = 10) => {
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  if (pct <= 20) return SCORE_CONFIG.qualificationEmojis.veryPoor.tooltip;
  if (pct <= 40) return SCORE_CONFIG.qualificationEmojis.poor.tooltip;
  if (pct <= 60) return SCORE_CONFIG.qualificationEmojis.average.tooltip;
  if (pct <= 80) return SCORE_CONFIG.qualificationEmojis.good.tooltip;
  return SCORE_CONFIG.qualificationEmojis.excellent.tooltip;
};

// Helper functions to check if special emojis should be shown
export const shouldShowCrown = (score) => score >= 90;
export const shouldShowSkull = (score) => score >= 0 && score <= 25;

// Helper functions for qualification scores (0-10 scale)
export const getQualificationScoreColor = (score, maxScore = 10) => {
  // Percentage-based so it scales with the configured qualification max (not a fixed 0-10 scale)
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  if (pct <= 20) return SCORE_CONFIG.colors.veryLow.background; // Red (very poor)
  if (pct <= 40) return SCORE_CONFIG.colors.low.background; // Amber (poor)
  if (pct <= 60) return SCORE_CONFIG.colors.medium.background; // Yellow (medium)
  if (pct <= 80) return SCORE_CONFIG.colors.high.background; // Green (good)
  return SCORE_CONFIG.colors.excellent.background; // Emerald (excellent)
};

export const getQualificationScoreTextColor = (score, maxScore = 10) => {
  // Percentage-based so it scales with the configured qualification max (not a fixed 0-10 scale)
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  if (pct <= 20) return SCORE_CONFIG.colors.veryLow.text; // Red (very poor)
  if (pct <= 40) return SCORE_CONFIG.colors.low.text; // Amber (poor)
  if (pct <= 60) return SCORE_CONFIG.colors.medium.text; // Yellow (medium)
  if (pct <= 80) return SCORE_CONFIG.colors.high.text; // Green (good)
  return SCORE_CONFIG.colors.excellent.text; // Emerald (excellent)
};

// Helper functions to check if special emojis should be shown for qualification scores
export const shouldShowQualificationCrown = (score, maxScore = 10) => {
  const percentage = (score / maxScore) * 100;
  return percentage >= 90;
};

export const shouldShowQualificationSkull = (score, maxScore = 10) => {
  const percentage = (score / maxScore) * 100;
  return percentage >= 0 && percentage <= 25;
};

// Point score helpers (percentage-based, works with any totalScore)
export const getPointScoreColor = (score, maxScore) => {
  if (!maxScore || maxScore <= 0) return SCORE_CONFIG.colors.medium.background;
  const pct = (score / maxScore) * 100;
  if (pct <= 25) return SCORE_CONFIG.colors.veryLow.background;
  if (pct <= 50) return SCORE_CONFIG.colors.low.background;
  if (pct <= 75) return SCORE_CONFIG.colors.medium.background;
  if (pct <= 89) return SCORE_CONFIG.colors.high.background;
  return SCORE_CONFIG.colors.excellent.background;
};

export const getPointScoreTextColor = (score, maxScore) => {
  if (!maxScore || maxScore <= 0) return SCORE_CONFIG.colors.medium.text;
  const pct = (score / maxScore) * 100;
  if (pct <= 25) return SCORE_CONFIG.colors.veryLow.text;
  if (pct <= 50) return SCORE_CONFIG.colors.low.text;
  if (pct <= 75) return SCORE_CONFIG.colors.medium.text;
  if (pct <= 89) return SCORE_CONFIG.colors.high.text;
  return SCORE_CONFIG.colors.excellent.text;
};

export const getPointScoreEmoji = (score, maxScore) => {
  if (!maxScore || maxScore <= 0) return SCORE_CONFIG.emojis.chart.emoji;
  const pct = (score / maxScore) * 100;
  if (pct >= 90) return SCORE_CONFIG.emojis.crown.emoji;
  if (pct <= 25) return SCORE_CONFIG.emojis.skull.emoji;
  return SCORE_CONFIG.emojis.chart.emoji;
};

// Hiring recommendation verdict, derived from the Gesamtscore percentage (0-100).
// Three buckets: Einladen (invite) / Unsicher (unsure) / Ablehnung (reject).
// `color` is an explicit hex (not a Tailwind class) so the badge renders reliably
// regardless of which utility classes are present in the prebuilt output.css.
// Default tiers; admins define their own set in the global settings
// (Einstellungen → Punktebewertung), persisted as settings.recommendation_thresholds.
export const DEFAULT_RECOMMENDATION_TIERS = [
  { label: 'Einladen', minScore: 70, color: '#15803d', emoji: '✅' },
  { label: 'Unsicher', minScore: 40, color: '#f59e0b', emoji: '❓' },
  { label: 'Ablehnung', minScore: 0, color: '#b91c1c', emoji: '⛔' }
];

const TIER_FALLBACK_COLORS = ['#15803d', '#65a30d', '#f59e0b', '#ea580c', '#b91c1c', '#7f1d1d'];

const clampPercent = (v) => Math.min(100, Math.max(0, Math.round(v)));

// Accepts anything (PocketBase JSON, form input, the legacy {invite, unsure}
// shape, undefined) and returns a non-empty tier list sorted from the highest
// cutoff down. The lowest tier always catches everything from 0.
export const normalizeRecommendationTiers = (raw) => {
  // Legacy two-threshold shape from the first version of this setting.
  if (raw && !Array.isArray(raw) && typeof raw === 'object' && ('invite' in raw || 'unsure' in raw)) {
    const invite = Number(raw.invite);
    const unsure = Number(raw.unsure);
    if (Number.isFinite(invite) && Number.isFinite(unsure)) {
      const normalizedUnsure = clampPercent(unsure);
      return [
        { ...DEFAULT_RECOMMENDATION_TIERS[0], minScore: Math.max(clampPercent(invite), normalizedUnsure) },
        { ...DEFAULT_RECOMMENDATION_TIERS[1], minScore: normalizedUnsure },
        { ...DEFAULT_RECOMMENDATION_TIERS[2] }
      ];
    }
    return DEFAULT_RECOMMENDATION_TIERS.map((tier) => ({ ...tier }));
  }

  if (!Array.isArray(raw)) return DEFAULT_RECOMMENDATION_TIERS.map((tier) => ({ ...tier }));

  const tiers = raw
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry, index) => {
      const minScore = Number(entry.minScore);
      return {
        label: String(entry.label || '').trim() || `Stufe ${index + 1}`,
        minScore: Number.isFinite(minScore) ? clampPercent(minScore) : 0,
        color: /^#[0-9a-fA-F]{3,8}$/.test(String(entry.color || ''))
          ? String(entry.color)
          : TIER_FALLBACK_COLORS[index % TIER_FALLBACK_COLORS.length],
        emoji: String(entry.emoji || '').trim()
      };
    });
  if (tiers.length === 0) return DEFAULT_RECOMMENDATION_TIERS.map((tier) => ({ ...tier }));

  tiers.sort((a, b) => b.minScore - a.minScore);
  tiers[tiers.length - 1] = { ...tiers[tiers.length - 1], minScore: 0 };
  return tiers;
};

// A pure function of the live score: the badge always reflects the current
// Gesamtscore (including manual overrides and Bonus/Malus). Red flags set at
// analysis time are shown per question (🚩) but never freeze the verdict.
// Returns the matched tier ({ label, minScore, color, emoji }).
export const getRecommendation = (score, tiers) => {
  const list = normalizeRecommendationTiers(tiers);
  const s = Number(score) || 0;
  return list.find((tier) => s >= tier.minScore) || list[list.length - 1];
};
